import React, { useContext, useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { useQuery } from '@tanstack/react-query';

import { Box, Paper, Tab, Tabs, Typography } from '@mui/material';

import { RootState } from '../../app/rootReducer';
import { Resource } from '../../common/constants/resource';
import { isUnderwriterUWX } from '../../common/constants/roles';
import { UserOrganization } from '../../common/constants/userOrganization';
import { ExistingPolicy, PastApplication } from '../../common/dataTypes';
import { ProductFactory } from '../../common/productConfig/productFactory';
import { getOwnerLinkedPolicies } from '../../common/services/ownerProfileService';
import { AuthContext } from '../login/AuthWrapper';
import styles from './LinkedPolicies.module.scss';
import { LinkedPoliciesItem, LinkedPolicyRow } from './LinkedPoliciesItem';

interface LinkedPoliciesData {
  activatedPolicies: ExistingPolicy[];
  maestroPolicies: PastApplication[];
}

export const LinkedPolicies = ({
  policyNumber,
  policies,
  fileOpenHandler,
  stackedCarrierPolicyNumbers,
  stackedTermPolicyIds,
  bundledCarrierPolicyNumber,
  bundledPolicyId,
}: {
  policyId: number;
  policyNumber: string;
  policies: LinkedPoliciesData | null;
  fileOpenHandler: (uri: string, fileName: string) => void;
  stackedCarrierPolicyNumbers: string[] | undefined;
  stackedTermPolicyIds: number[] | undefined;
  bundledCarrierPolicyNumber?: string;
  bundledPolicyId?: number;
}) => {
  const { product, subCategory, carrier, applicationId } = useSelector(
    (state: RootState) => ({
      product: state.application.product,
      subCategory: state.application.subCategory,
      carrier: state.application.carrier,
      applicationId: state.application.applicationId,
    }),
    shallowEqual,
  );

  const productConfig = ProductFactory.getProductConfig(
    carrier,
    product,
    subCategory ?? '',
  );
  const isOwnerInsuredSplitVariant =
    productConfig?.shouldRenderOwnerLinkedPolicies() ?? false;

  // Juvenile products (e.g. North American NAIULACUM) split this into two
  // sections: the owner's own linked policies, and the insured's — the owner
  // data comes from a separate identity-based lookup, not the `policies` prop.
  const { data: ownerPoliciesData } = useQuery({
    queryKey: ['linkedPoliciesOwnerData', applicationId],
    queryFn: () => getOwnerLinkedPolicies(String(applicationId)),
    enabled: isOwnerInsuredSplitVariant && !!applicationId,
  });

  const hasOwnerPolicies =
    (ownerPoliciesData?.activatedPolicies?.length ?? 0) > 0 ||
    (ownerPoliciesData?.maestroPolicies?.length ?? 0) > 0;

  if (isOwnerInsuredSplitVariant) {
    return (
      <div
        data-label={'existing_policies'}
        data-section-id={'Existing policies'}
        className={styles.LinkedPolicies}
      >
        {hasOwnerPolicies && (
          <>
            <LinkedPoliciesPanel
              sectionLabel="Owner"
              policyNumber={undefined}
              policies={ownerPoliciesData ?? null}
              fileOpenHandler={fileOpenHandler}
              stackedCarrierPolicyNumbers={undefined}
              stackedTermPolicyIds={undefined}
            />
            <Box className={styles.SectionSpacer} />
          </>
        )}
        <LinkedPoliciesPanel
          sectionLabel="Insured"
          policyNumber={policyNumber}
          policies={policies}
          fileOpenHandler={fileOpenHandler}
          stackedCarrierPolicyNumbers={stackedCarrierPolicyNumbers}
          stackedTermPolicyIds={stackedTermPolicyIds}
          bundledCarrierPolicyNumber={bundledCarrierPolicyNumber}
          bundledPolicyId={bundledPolicyId}
        />
      </div>
    );
  }

  return (
    <div
      data-label={'existing_policies'}
      data-section-id={'Existing policies'}
      className={styles.LinkedPolicies}
    >
      <LinkedPoliciesPanel
        policyNumber={policyNumber}
        policies={policies}
        fileOpenHandler={fileOpenHandler}
        stackedCarrierPolicyNumbers={stackedCarrierPolicyNumbers}
        stackedTermPolicyIds={stackedTermPolicyIds}
        bundledCarrierPolicyNumber={bundledCarrierPolicyNumber}
        bundledPolicyId={bundledPolicyId}
      />
    </div>
  );
};

function LinkedPoliciesPanel({
  sectionLabel,
  policyNumber,
  policies,
  fileOpenHandler,
  stackedCarrierPolicyNumbers,
  stackedTermPolicyIds,
  bundledCarrierPolicyNumber,
  bundledPolicyId,
}: {
  // When set, renders as "Linked Policies  <sectionLabel>" (owner/insured split).
  // When absent, renders the plain "Linked Policies" heading.
  sectionLabel?: string;
  policyNumber: string | undefined;
  policies: LinkedPoliciesData | null;
  fileOpenHandler: (uri: string, fileName: string) => void;
  stackedCarrierPolicyNumbers: string[] | undefined;
  stackedTermPolicyIds: number[] | undefined;
  bundledCarrierPolicyNumber?: string;
  bundledPolicyId?: number;
}) {
  const auth = useContext(AuthContext);
  // null until the user explicitly picks a tab, so the default tab tracks
  // which data is actually available (e.g. past-only policies default to
  // the "past" tab instead of rendering an empty "existing" tab).
  const [tab, setTab] = useState<'existing' | 'past' | null>(null);

  const canUserViewPastApplications =
    auth.userOrganization === UserOrganization.ETHOS &&
    !isUnderwriterUWX(auth?.roles);

  const stackedRows: LinkedPolicyRow[] = useMemo(() => {
    const rows: LinkedPolicyRow[] = [];

    (stackedCarrierPolicyNumbers ?? []).forEach(carrierPolicyNumber => {
      const match = (policies?.activatedPolicies ?? []).find(
        p => p.carrierPolicyNumber === carrierPolicyNumber,
      );
      rows.push(
        toExistingRow(
          match,
          carrierPolicyNumber,
          undefined,
          bundledCarrierPolicyNumber,
        ),
      );
    });

    (stackedTermPolicyIds ?? []).forEach(termPolicyId => {
      const match = (policies?.activatedPolicies ?? []).find(
        p => p.termPolicyId === termPolicyId,
      );
      rows.push(
        toExistingRow(
          match,
          match?.carrierPolicyNumber,
          termPolicyId,
          bundledCarrierPolicyNumber,
        ),
      );
    });

    return rows;
  }, [
    policies,
    stackedCarrierPolicyNumbers,
    stackedTermPolicyIds,
    bundledCarrierPolicyNumber,
  ]);

  const activatedRows: LinkedPolicyRow[] = useMemo(() => {
    return (policies?.activatedPolicies ?? [])
      .filter(policy => {
        if (policy.carrierPolicyNumber === policyNumber) return false;
        if (stackedCarrierPolicyNumbers?.includes(policy.carrierPolicyNumber))
          return false;
        if (
          policy.termPolicyId &&
          stackedTermPolicyIds?.includes(policy.termPolicyId)
        )
          return false;
        return true;
      })
      .map(policy =>
        toExistingRow(
          policy,
          policy.carrierPolicyNumber,
          policy.termPolicyId,
          bundledCarrierPolicyNumber,
        ),
      );
  }, [
    policies,
    stackedCarrierPolicyNumbers,
    stackedTermPolicyIds,
    policyNumber,
    bundledCarrierPolicyNumber,
  ]);

  const pastApplicationRows: LinkedPolicyRow[] = useMemo(() => {
    return (policies?.maestroPolicies ?? []).map(policy =>
      toPastApplicationRow(
        policy,
        auth.userOrganization === UserOrganization.ETHOS,
        bundledPolicyId,
      ),
    );
  }, [policies, auth.userOrganization, bundledPolicyId]);

  const existingRows = [...stackedRows, ...activatedRows];

  const hasExisting = existingRows.length > 0;
  const hasPast = pastApplicationRows.length > 0 && canUserViewPastApplications;

  if (!hasExisting && !hasPast) {
    return null;
  }

  const activeTab = tab ?? (hasExisting || !hasPast ? 'existing' : 'past');
  const activeRows = activeTab === 'past' ? pastApplicationRows : existingRows;

  return (
    <>
      <Box>
        <Typography variant="h2">
          {Resource.TASK_TITLE_LINKED_POLICIES}
          {sectionLabel && (
            <>
              &nbsp;&nbsp;
              <Typography component="span" variant="h2" fontWeight="bold">
                {sectionLabel}
              </Typography>
            </>
          )}
        </Typography>
      </Box>
      <Box className={styles.Spacer} />
      <Paper variant="outlined" className={styles.Panel}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setTab(value)}
          className={styles.Tabs}
        >
          <Tab
            value="existing"
            label={
              <span>
                {Resource.TASK_TITLE_EXISTING_POLICIES}
                <span className={styles.CountBadge}>{existingRows.length}</span>
              </span>
            }
          />
          {hasPast && (
            <Tab
              value="past"
              label={
                <span>
                  {Resource.TASK_TITLE_PAST_APPLICATIONS}
                  <span className={styles.CountBadge}>
                    {pastApplicationRows.length}
                  </span>
                </span>
              }
            />
          )}
        </Tabs>
        <div className={styles.ItemList}>
          {activeRows.map((row, index) => (
            <LinkedPoliciesItem
              key={row.key}
              row={row}
              fileOpenHandler={fileOpenHandler}
              defaultExpanded={index === 0}
            />
          ))}
        </div>
      </Paper>
    </>
  );
}

export function toExistingRow(
  policy: ExistingPolicy | undefined,
  carrierPolicyNumber: string | undefined,
  termPolicyId: number | undefined,
  bundledCarrierPolicyNumber: string | undefined,
): LinkedPolicyRow {
  const key = carrierPolicyNumber ?? String(termPolicyId);
  if (!policy) {
    return {
      key,
      policyNumberLabel: carrierPolicyNumber ?? String(termPolicyId),
      notFound: true,
    };
  }

  const linkTo = policy.applicationId
    ? `/workbench/applications/${policy.applicationId}`
    : policy.termPolicyId
      ? `/workbench/ppwb/policy/${policy.termPolicyId}`
      : undefined;

  return {
    key,
    policyNumberLabel: policy.carrierPolicyNumber,
    linkTo,
    applicationId: policy.applicationId,
    termPolicyId: policy.termPolicyId,
    isBundled:
      !!bundledCarrierPolicyNumber &&
      policy.carrierPolicyNumber === bundledCarrierPolicyNumber,
    product: policy.product,
    carrier: policy.carrier,
    subCategory: policy.subCategory,
    decision: policy.lastRelevantDecision?.decision,
    decisionType: policy.lastRelevantDecision?.decisionType,
    status: policy.status,
    effectiveDate: policy.effectiveDate,
    coverage: policy.coverage,
    term: policy.term,
    stageV2: policy.stageV2,
    detailedLifeCycleV2: policy.detailedLifeCycleV2,
    rateClass: policy.lastRelevantDecision?.rateClass,
    debits: policy.lastRelevantDecision?.debits,
    tobaccoClass: policy.lastRelevantDecision?.tobaccoClass,
    decisionDate: policy.lastRelevantDecision?.createdAt,
  };
}

export function toPastApplicationRow(
  policy: PastApplication,
  isEthosOrg: boolean,
  bundledPolicyId: number | undefined,
): LinkedPolicyRow {
  const linkTo = policy.applicationId
    ? `/workbench/applications/${policy.applicationId}`
    : isEthosOrg
      ? `/workbench/ppwb/policy/${policy.policyId}`
      : undefined;

  return {
    key: String(policy.policyId),
    policyNumberLabel: policy.carrierPolicyNumber ?? String(policy.policyId),
    linkTo,
    applicationId: policy.applicationId,
    termPolicyId: policy.policyId,
    isBundled: bundledPolicyId != null && policy.policyId === bundledPolicyId,
    product: policy.product,
    carrier: policy.carrier,
    subCategory: policy.subCategory,
    decision: policy.lastRelevantDecision?.decision,
    decisionType: policy.lastRelevantDecision?.decisionType,
    coverage: policy.coverage,
    term: policy.term,
    stageV2: policy.stageV2,
    detailedLifeCycleV2: policy.detailedLifeCycleV2,
    rateClass: policy.lastRelevantDecision?.rateClass,
    debits: policy.lastRelevantDecision?.debits,
    tobaccoClass: policy.lastRelevantDecision?.tobaccoClass,
    decisionDate: policy.lastRelevantDecision?.createdAt,
  };
}
